import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { Plus, ChevronLeft, ChevronRight, RefreshCw, Search, Wrench, X } from "lucide-react";
import { toast } from "sonner";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "./appointments.css";

import { api } from "@/lib/api";
import { offlineSafe } from "@/lib/api";
import AppointmentDialog from "./components/AppointmentDialog";
import ConvertToJobCardDialog from "./components/ConvertToJobCardDialog";
import { STATUS_META, CALENDAR_DEFAULTS, isoNaive, toDate, prettyDuration, fmtTime, normalizeStatus, allowedWindow, DEFAULT_WORKING_HOURS } from "./lib";

const DnDCalendar = withDragAndDrop(BigCalendar);

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales,
});

// Working-hours grid: 08:00 → 20:00 with 30-min slots (per user choice).
const MIN_TIME = new Date();
MIN_TIME.setHours(CALENDAR_DEFAULTS.minHour, 0, 0, 0);
const MAX_TIME = new Date();
MAX_TIME.setHours(CALENDAR_DEFAULTS.maxHour, 0, 0, 0);

// Local cache key for offline first-paint
const CACHE_KEY = "fender_appts_cache_v1";

// Map an appointment doc → react-big-calendar event
const toEvent = (a) => {
  const start = toDate(a.scheduled_at) || new Date();
  const end = new Date(start.getTime() + Number(a.duration_min || 60) * 60000);
  return {
    id: a.id,
    title: a.customer_snapshot?.name || "Untitled",
    start,
    end,
    allDay: false,
    resource: a,
  };
};

export default function AppointmentsCalendar() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("week");                    // month | week | day | list
  const [listTab, setListTab] = useState("upcoming");          // upcoming | past
  const [date, setDate] = useState(new Date());
  const [q, setQ] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);
  const [error, setError] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(null);     // { start, end }
  const [editing, setEditing] = useState(null);       // full appt doc
  const [converting, setConverting] = useState(null); // full appt doc
  const dragOccurred = useRef(false);

  // ---------- Data fetching ----------
  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/appointments", { params });
      setRows(r.data || []);
      setError(null);
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), rows: r.data || [] }));
      } catch { /* ignore quota */ }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load appointments");
    } finally {
      setLoading(false);
    }
  }, [q]);

  // Cache-first paint
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
      if (cached?.rows?.length) {
        setRows(cached.rows);
        setLoading(false);
      }
    } catch { /* noop */ }
  }, []);

  // Fetch on filter change (debounced for search)
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
     
  }, [q]);

  // Reload on reconnect
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [load]);

  // Assignees for the form (still needed even though the filter dropdown is gone)
  useEffect(() => {
    api.get("/appointments/assignees").then((r) => setAssignees(r.data || [])).catch(() => setAssignees([]));
    // Working hours for the ±2h scheduling guard.  Silently fall back to
    // 09:00–20:00 if the caller can't read settings (e.g. mechanic role).
    api.get("/settings/preferences")
      .then((r) => setWorkingHours(r.data?.working_hours || DEFAULT_WORKING_HOURS))
      .catch(() => setWorkingHours(DEFAULT_WORKING_HOURS));
  }, []);

  // Auto-open the "New Appointment" dialog when the Quick-Create menu sends
  // us here with `?new=1`.  We strip the flag from the URL so a refresh
  // doesn't retrigger the dialog.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("new") === "1") {
      const start = new Date();
      // Round to the next 30-min slot so the picker snaps cleanly.
      start.setMinutes(start.getMinutes() < 30 ? 30 : 60, 0, 0);
      setCreating({ start, end: null });
      setOpenCreate(true);
      params.delete("new");
      const cleaned = params.toString() ? `?${params.toString()}` : "";
      window.history.replaceState({}, "", window.location.pathname + cleaned);
    }
  }, []);

  const events = useMemo(() => rows.map(toEvent), [rows]);

  // ---------- Reminder toast: notify for appointments starting within 60 min ----------
  const notifiedRef = useRef(new Set());
  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      for (const a of rows) {
        if (normalizeStatus(a.status) !== "scheduled") continue;
        const start = toDate(a.scheduled_at)?.getTime();
        if (!start) continue;
        const mins = Math.floor((start - now) / 60000);
        // Fire between 0 and 60 minutes ahead, once per appointment
        if (mins > 0 && mins <= 60 && !notifiedRef.current.has(a.id)) {
          notifiedRef.current.add(a.id);
          toast.info(
            `⏰ ${a.customer_snapshot?.name || "Appointment"} in ${mins}m`,
            {
              description: `${fmtTime(a.scheduled_at)} · ${a.vehicle_snapshot?.reg_number || "no vehicle"} · ${prettyDuration(a.duration_min)}`,
              duration: 8000,
            },
          );
        }
      }
    };
    tick();
    const iv = setInterval(tick, 60_000);
    return () => clearInterval(iv);
  }, [rows]);

  // ---------- Handlers ----------
  const eventPropGetter = useCallback((event) => {
    const key = normalizeStatus(event.resource?.status);
    const meta = STATUS_META[key] || STATUS_META.scheduled;
    return {
      style: {
        backgroundColor: meta.event,
        color: "white",
        borderColor: meta.event,
      },
    };
  }, []);

  const dayPropGetter = useCallback((d) => {
    const isToday = d.toDateString() === new Date().toDateString();
    if (isToday) return { style: { background: "#a8dadc26" } };
    return {};
  }, []);

  const onSelectSlot = useCallback((slot) => {
    if (dragOccurred.current) { dragOccurred.current = false; return; }
    // Snap the start to the nearest 30-min slot inside working hours
    const start = new Date(slot.start);
    const end = slot.end ? new Date(slot.end) : new Date(start.getTime() + 60 * 60000);
    setCreating({ start, end });
    setOpenCreate(true);
  }, []);

  const onSelectEvent = useCallback((event) => {
    dragOccurred.current = false;
    setEditing(event.resource);
  }, []);

  const onEventDrop = useCallback(async ({ event, start, end }) => {
    dragOccurred.current = true;
    const a = event.resource;
    // Lock: JC-linked appointments and cancelled ones can't be rescheduled.
    if (a.converted_to_job_card_id || a.status === "cancelled" || a.status === "completed") {
      toast.error("Cannot reschedule a completed/cancelled appointment");
      return;
    }
    const newStart = isoNaive(new Date(start));
    const newDuration = Math.max(15, Math.round((new Date(end) - new Date(start)) / 60000));
    // Optimistic update
    setRows((prev) => prev.map((r) => r.id === a.id ? { ...r, scheduled_at: newStart, duration_min: newDuration } : r));
    try {
      const resp = await offlineSafe("PATCH", `/appointments/${a.id}/reschedule`, {
        scheduled_at: newStart, duration_min: newDuration,
      }, `Reschedule ${a.number}`);
      if (resp?._queued) toast.info("Rescheduled offline — will sync when online");
      else toast.success(`Rescheduled ${a.number || a.customer_snapshot?.name}`);
    } catch (e) {
      // rollback
      setRows((prev) => prev.map((r) => r.id === a.id ? a : r));
      toast.error(e.response?.data?.detail || "Could not reschedule");
    }
  }, []);

  const onEventResize = useCallback(async ({ event, start, end }) => {
    dragOccurred.current = true;
    const a = event.resource;
    if (a.converted_to_job_card_id || a.status === "cancelled" || a.status === "completed") return;
    const newStart = isoNaive(new Date(start));
    const newDuration = Math.max(15, Math.round((new Date(end) - new Date(start)) / 60000));
    setRows((prev) => prev.map((r) => r.id === a.id ? { ...r, scheduled_at: newStart, duration_min: newDuration } : r));
    try {
      const resp = await offlineSafe("PATCH", `/appointments/${a.id}/reschedule`, {
        scheduled_at: newStart, duration_min: newDuration,
      }, `Resize ${a.number}`);
      if (resp?._queued) toast.info("Saved offline");
      else toast.success(`Duration updated to ${prettyDuration(newDuration)}`);
    } catch (e) {
      setRows((prev) => prev.map((r) => r.id === a.id ? a : r));
      toast.error(e.response?.data?.detail || "Could not resize");
    }
  }, []);

  // ---------- Save / delete handlers from the dialog ----------
  const onSaved = useCallback((saved, deletedId) => {
    if (deletedId) {
      setRows((prev) => prev.filter((r) => r.id !== deletedId));
    } else if (saved) {
      setRows((prev) => {
        const has = prev.some((r) => r.id === saved.id);
        return has ? prev.map((r) => (r.id === saved.id ? { ...r, ...saved } : r)) : [...prev, saved];
      });
    }
  }, []);

  const onConverted = useCallback((res) => {
    // Once converted to a JC, the appointment status flips to `completed`
    // and gets a permanent link to the job card.
    setRows((prev) => prev.map((r) => r.id === (converting?.id) ? {
      ...r, status: "completed",
      converted_to_job_card_id: res.job_card_id,
      converted_at: new Date().toISOString(),
    } : r));
    setEditing(null);
  }, [converting]);

  // Toolbar navigation — uses functional setState so navigation is reliable
  // even under batched updates when the view has just changed.
  const go = (delta) => {
    setDate((prev) => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() + delta);
      else if (view === "week" || view === "list") d.setDate(d.getDate() + 7 * delta);
      else d.setDate(d.getDate() + delta);
      return d;
    });
  };

  // Header label is computed inline in JSX to sidestep the Emergent Studio
  // preview overlay's variable-name-based textContent hijacking (which would
  // otherwise freeze the header on the initial-render value).
  const labelForCompact = (() => {
    if (view === "month") return date.toLocaleDateString([], { month: "long", year: "numeric" });
    if (view === "week") {
      const s = startOfWeek(date, { weekStartsOn: 1 });
      const e = new Date(s); e.setDate(e.getDate() + 6);
      const o = { day: "2-digit", month: "short" };
      return `${s.toLocaleDateString([], o)} – ${e.toLocaleDateString([], o)}, ${e.getFullYear()}`;
    }
    if (view === "list") return listTab === "past" ? "Past appointments" : "Upcoming appointments";
    return date.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  })();

  // KPI strip
  const kpis = useMemo(() => {
    const today = new Date().toDateString();
    let todayCount = 0, weekCount = 0;
    const wkStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const wkEnd = new Date(wkStart); wkEnd.setDate(wkEnd.getDate() + 7);
    for (const r of rows) {
      const dt = toDate(r.scheduled_at);
      if (!dt) continue;
      if (dt.toDateString() === today) todayCount++;
      if (dt >= wkStart && dt < wkEnd) weekCount++;
    }
    return { todayCount, weekCount };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="appointments-page">
      {/* Page header */}
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h1 className="text-[24px] md:text-[28px] font-bold text-[#1d3557] leading-tight">Appointments</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            Plan customer visits. Drag to reschedule, click any slot to book.
          </p>
        </div>
        <button
          onClick={() => { setCreating({ start: new Date(), end: null }); setOpenCreate(true); }}
          data-testid="appt-new-btn"
          className="h-11 px-4 rounded-lg bg-[#e63946] hover:bg-[#d02f3c] text-white text-[13px] font-semibold inline-flex items-center gap-2 self-start"
        >
          <Plus size={16} /> New Appointment
        </button>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
        <Kpi label="Today" value={kpis.todayCount} testId="appt-kpi-today" />
        <Kpi label="This week" value={kpis.weekCount} testId="appt-kpi-week" tone="blue" />
      </div>

      {/* Prominent date header — bold, large, and always readable regardless of view */}
      <div className="flex items-baseline gap-3">
        <h2
          key={`hdr-${view}-${date.getTime()}-${listTab}`}
          className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight"
          data-testid="appt-header-label"
        >
          {(() => {
            if (view === "month") return date.toLocaleDateString([], { month: "long", year: "numeric" });
            if (view === "week") {
              const s = startOfWeek(date, { weekStartsOn: 1 });
              const e = new Date(s); e.setDate(e.getDate() + 6);
              const o = { day: "2-digit", month: "short" };
              return `${s.toLocaleDateString([], o)} – ${e.toLocaleDateString([], o)}, ${e.getFullYear()}`;
            }
            if (view === "list") return listTab === "past" ? "Past appointments" : "Upcoming appointments";
            return date.toLocaleDateString([], { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
          })()}
        </h2>
        {view !== "list" && (
          <span className="text-[12.5px] text-[#667085] hidden md:inline capitalize">
            {view} view
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-[#E4E7EC] p-2 flex flex-wrap items-center gap-2">
        {/* Nav */}
        <div className="flex items-center gap-1">
          <button
            className="h-9 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE]"
            onClick={() => setDate(new Date())}
            data-testid="appt-today"
          >Today</button>
          <button
            className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#457b9d] hover:bg-[#F1FAEE] disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => go(-1)}
            data-testid="appt-prev"
            aria-label="Previous"
            disabled={view === "list"}
          ><ChevronLeft size={15} /></button>
          <button
            className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#457b9d] hover:bg-[#F1FAEE] disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => go(1)}
            data-testid="appt-next"
            aria-label="Next"
            disabled={view === "list"}
          ><ChevronRight size={15} /></button>
        </div>

        {/* Compact secondary label (kept for parity with older tests) */}
        <div className="text-[13px] font-semibold text-[#457b9d] px-2 py-1 hidden lg:inline-block" data-testid="appt-label">{labelForCompact}</div>

        <div className="flex-1" />

        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#a8dadc]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="appt-search"
            placeholder="Search customer / vehicle…"
            className="w-52 h-9 pl-8 pr-8 rounded-md border border-[#E4E7EC] text-[12.5px] focus:border-[#457b9d] outline-none bg-white"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#a8dadc] hover:text-[#e63946]"
              type="button"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* View toggle */}
        <div className="bg-[#F7F9FC] rounded-md p-1 flex" data-testid="appt-view-toggle">
          {["month", "week", "day", "list"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              data-testid={`appt-view-${v}`}
              className={`h-7 px-3 rounded text-[11.5px] font-semibold capitalize transition-colors ${
                view === v ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <button
          onClick={() => load()}
          className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#457b9d] hover:bg-[#F1FAEE]"
          data-testid="appt-refresh"
          aria-label="Refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]" data-testid="appt-error">
          {error}
        </div>
      )}

      {/* Calendar OR list view */}
      {view === "list" ? (
        <AppointmentList
          rows={rows}
          q={q}
          listTab={listTab}
          setListTab={setListTab}
          onRowClick={(a) => setEditing(a)}
        />
      ) : (
        <section className="appt-shell" data-testid="appt-calendar">
          <DnDCalendar
            localizer={localizer}
            events={events}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            startAccessor="start"
            endAccessor="end"
            selectable
            resizable
            step={CALENDAR_DEFAULTS.slotMinutes}
            timeslots={2}
            min={MIN_TIME}
            max={MAX_TIME}
            onSelectSlot={onSelectSlot}
            onSelectEvent={onSelectEvent}
            onEventDrop={onEventDrop}
            onEventResize={onEventResize}
            eventPropGetter={eventPropGetter}
            dayPropGetter={dayPropGetter}
            style={{ height: "calc(100vh - 340px)", minHeight: "560px" }}
            components={{
              event: EventChip,
              toolbar: () => null,
            }}
            formats={{
              timeGutterFormat: "h a",
              eventTimeRangeFormat: () => "",
              dayFormat: (d, culture, loc) => loc.format(d, "EEE d", culture),
              weekdayFormat: (d, culture, loc) => loc.format(d, "EEE", culture),
            }}
            messages={{
              noEventsInRange: "No appointments in this range.",
              showMore: (n) => `+${n} more`,
            }}
          />
        </section>
      )}

      {/* Create / edit dialogs */}
      <AppointmentDialog
        open={openCreate}
        onClose={() => { setOpenCreate(false); setCreating(null); }}
        defaultStart={creating?.start}
        defaultEnd={creating?.end}
        assignees={assignees}
        workingHours={workingHours}
        onSaved={onSaved}
      />
      <AppointmentDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        initial={editing}
        assignees={assignees}
        workingHours={workingHours}
        onSaved={onSaved}
        onConvert={(a) => { setEditing(null); setConverting(a); }}
      />
      <ConvertToJobCardDialog
        open={!!converting}
        appointment={converting}
        assignees={assignees}
        onClose={() => setConverting(null)}
        onConverted={onConverted}
      />
    </div>
  );
}

function Kpi({ label, value, tone = "steel", testId }) {
  const toneMap = {
    steel: "bg-white border-[#E4E7EC]",
    blue: "bg-[#a8dadc]/20 border-[#a8dadc]",
    green: "bg-[#F1FAEE] border-[#12B76A]/20",
    amber: "bg-[#FEF0C7] border-[#F79009]/25",
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className="text-[26px] font-bold text-[#1d3557] font-mono tabular-nums mt-1">{value}</div>
    </div>
  );
}

function EventChip({ event }) {
  const a = event.resource || {};
  const isTimeGrid = event.start && event.end && (event.end - event.start >= 15 * 60000);
  const start = event.start;
  const end = event.end;
  const timeLabel = start
    ? `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
    : "";
  return (
    <div className="appt-event" data-testid={`appt-event-${a.id}`}>
      {isTimeGrid && <div className="appt-event-time">{timeLabel}</div>}
      <div className="appt-event-title">
        {a.customer_snapshot?.name || event.title}
        {a.converted_to_job_card_id && <Wrench size={9} className="inline ml-1 -mt-0.5" />}
      </div>
      {(a.vehicle_snapshot?.reg_number) && (
        <div className="appt-event-sub">
          {a.vehicle_snapshot.reg_number}
          {a.duration_min ? ` · ${prettyDuration(a.duration_min)}` : ""}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// List view — flat, sortable table with two tabs (Upcoming | Past).
// ---------------------------------------------------------------------------
function AppointmentList({ rows, q, listTab, setListTab, onRowClick }) {
  const filtered = useMemo(() => {
    const now = new Date();
    const needle = q.trim().toLowerCase();
    const items = rows
      .map((r) => ({ ...r, _when: toDate(r.scheduled_at) }))
      .filter((r) => r._when);
    // Split by upcoming vs past based on today's date (not the row's status,
    // so a completed job that was scheduled for tomorrow still shows under
    // Upcoming).
    const parted = items.filter((r) => (listTab === "past" ? r._when < now : r._when >= now));
    const searched = needle
      ? parted.filter((r) =>
          (r.number || "").toLowerCase().includes(needle) ||
          (r.customer_snapshot?.name || "").toLowerCase().includes(needle) ||
          (r.customer_snapshot?.phone || "").toLowerCase().includes(needle) ||
          (r.vehicle_snapshot?.reg_number || "").toLowerCase().includes(needle))
      : parted;
    // Upcoming ascending (soonest first), past descending (most recent first).
    searched.sort((a, b) => listTab === "past" ? (b._when - a._when) : (a._when - b._when));
    return searched;
  }, [rows, listTab, q]);

  return (
    <section
      className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden"
      data-testid="appt-list-view"
    >
      {/* Tab strip */}
      <div className="flex items-center gap-1 border-b border-[#E4E7EC] px-3 py-2 bg-[#F7F9FC]">
        {[
          { key: "upcoming", label: "Upcoming" },
          { key: "past", label: "Past" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setListTab(t.key)}
            data-testid={`appt-list-tab-${t.key}`}
            className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
              listTab === t.key
                ? "bg-[#1d3557] text-white"
                : "text-[#667085] hover:text-[#1d3557] hover:bg-white"
            }`}
          >
            {t.label}
            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              listTab === t.key ? "bg-white/20" : "bg-[#e63946]/10 text-[#e63946]"
            }`}>
              {t.key === listTab ? filtered.length : (
                rows.filter((r) => {
                  const w = toDate(r.scheduled_at);
                  if (!w) return false;
                  return t.key === "past" ? w < new Date() : w >= new Date();
                }).length
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="p-8 text-center text-[13px] text-[#667085]" data-testid="appt-list-empty">
          {listTab === "past" ? "No past appointments to show." : "No upcoming appointments — book one from the calendar."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="appt-list-table">
            <thead className="bg-[#F1FAEE] text-[11px] uppercase tracking-wider text-[#457b9d]">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Number</th>
                <th className="text-left px-4 py-2.5 font-semibold">When</th>
                <th className="text-left px-4 py-2.5 font-semibold">Customer</th>
                <th className="text-left px-4 py-2.5 font-semibold">Vehicle</th>
                <th className="text-left px-4 py-2.5 font-semibold">Assignee</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const st = normalizeStatus(a.status);
                const meta = STATUS_META[st] || STATUS_META.scheduled;
                const dt = a._when;
                return (
                  <tr
                    key={a.id}
                    onClick={() => onRowClick(a)}
                    data-testid={`appt-list-row-${a.id}`}
                    className="border-t border-[#E4E7EC] cursor-pointer hover:bg-[#F7F9FC]"
                  >
                    <td className="px-4 py-2.5 font-mono text-[#457b9d]">{a.number}</td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-[#1d3557]">
                        {dt.toLocaleDateString([], { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                      </div>
                      <div className="text-[11.5px] text-[#667085]">
                        {dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                        {a.duration_min ? ` · ${prettyDuration(a.duration_min)}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-[#1d3557]">{a.customer_snapshot?.name || "—"}</div>
                      <div className="text-[11.5px] text-[#667085]">{a.customer_snapshot?.phone || ""}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-[#1d3557]">{a.vehicle_snapshot?.reg_number || "—"}</div>
                      <div className="text-[11.5px] text-[#667085]">
                        {[a.vehicle_snapshot?.make, a.vehicle_snapshot?.model].filter(Boolean).join(" ")}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[#457b9d]">{a.assignee?.name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}>
                        {meta.label}
                      </span>
                      {a.converted_to_job_card_id && <Wrench size={11} className="inline ml-2 text-[#457b9d]" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
